export type CateringChefMenuItem = {
  chefId: string
  chefPrice: number
  childItems: CateringChefMenuItem[]
  containsAlcohol: boolean | null
  cost: string
  description: string | null
  id: string
  internalNotes: string
  isApproved: boolean
  isEnabled: boolean
  isSubMenuItem: boolean
  itemType: string
  mealType: string
  menuItemId: string
  minQty: number | null
  name: string
  packaging?: {
    active: boolean
    costPerOrder: string
    createdAt: string
    defaultUnitCost: string
    dftHungryVipRecoupPct: string
    dftRecoupPct: string
    dftSupplyUsageRate: string
    id: string
    interchangeableType: string
    isDefault: boolean
    name: string
    supplyType: string
    trackInventory: boolean
    updatedAt: string
  }
  parentMenuItemId: string | null
  price: number
  retailPrice: number
  servingSize: string
  servingsPerPkg: number
  subMenuItemCategory: string | null
  tagsCuisineList: string[]
  tagsDietaryPreferenceList: string[]
  vendorDescription: string
}

export type ItemPickerMenuItem = {
  id: string
  name: string
  price: number
  mealType: string
  description: string | null
  tagsDietaryPreference: string[]
}

export type NonFoodItem = {
  id: string
  name: string
  cost: number
  price: number
  description: string | null
  itemType: string
  chefId: string

  // svc item
  serviceItemId?: string
  quantity?: number
  service_type?: string
  pricing_rate?: string | number | null

  // virtual item
  virtualItemId?: string

  // virtual kit
  virtualKitId?: string

  // snack pack
  snackPackId?: string
}

export type ServiceItem = NonFoodItem & {
  chefId: string
  serviceItemId?: string
  quantity: number
  service_type: string
  pricing_rate: string | number | null
}

export type VirtualItem = NonFoodItem & {
  virtualItemId?: string
}

export type VirtualKit = NonFoodItem & {
  virtualKitId?: string
}

export type SnackPack = NonFoodItem & {
  snackPackId?: string
}

export type ChefAuditItem = {
  chefId: string
  chefPrice: number
  childItems: ChefAuditItem[]
  id: string
  isSubMenuItem: boolean
  marketType: string
  mealType: string
  name: string
  parentMenuItemId: string | null
  tagsGroupOrderMealTypeList: string

  // Verifications
  allergensVerified: boolean
  descriptionVerified: boolean
  imageVerified: boolean
  servingDetailsVerified: boolean

  itemVerificationsCompleted: boolean
  allChildVerificationsCompleted: boolean
}

export type ChefMenuItemImage = {
  id: string
  order: number
  url: string
  thumborUrl: string
}

export type ChefSearchMenuItem = {
  id: string
  name: string
  description: string
  chefId: string
  pricePerPersonCalc: string
  mealType: string
  isEnabled: boolean
  isApproved: boolean
  isSubMenuItem: boolean
  subMenuItemCategory: string | null
  parentMenuItemId: string | null
  marketType: string
  isHot: boolean
  countPerShelf: number | null
  updatedAt: string
  inputType: string
  creatorId: string
  internalNotes: string | null
  allergensVerified: boolean
  descriptionVerified: boolean
  imageVerified: boolean
  servingDetailsVerified: boolean
  hasMods: boolean
  addedBy: string
  tagsDietaryPreferenceList: string
  tagsCuisineList: string
  tagsGroupOrderMealTypeList: string
  chefPrice: string
  marketPrice: string
  images: ChefMenuItemImage[]
}

export type ChefConceptMenuItem = {
  id: string
  name: string
  description: string | null
  price: string
  createdAt: string
  updatedAt: string
  imagesCount: number
  isEnabled: boolean
  isApproved: boolean
  isPending: boolean
  mealType: string
  isSubMenuItem: boolean
  subMenuItemCategory: string | null
  parentMenuItemId: string | null
  isDestroyed: boolean
  itemDetailableId: string
  itemDetailableType: string
  pricingRate: string | null
  cost: string
  chefId: string
  minQty: number
  virtualItemType: string | null
  containsAlcohol: boolean | null
  barcode: string | null
  dailyOrderLimit: number | null
  servingSize: string
  isHot: boolean
  countPerShelf: number | null
  retailPrice: string
  inputType: string
  creatorId: string
  creationOrderId: string | null
  creationProposalId: string | null
  internalNotes: string | null
  vendorDescription: string
  pricePerPersonCalc: string
  vendorNotes: string | null
  servingWeightOzs: number | null
  allergensVerified: boolean
  descriptionVerified: boolean
  imageVerified: boolean
  servingDetailsVerified: boolean
  allergensVerifiedBy: string
  descriptionVerifiedBy: string
  imageVerifiedBy: string | null
  servingDetailsVerifiedBy: string
  allergensVerifiedAt: string
  descriptionVerifiedAt: string
  imageVerifiedAt: string | null
  servingDetailsVerifiedAt: string
  imageSourceType: string | null
  isEcoPackaging: boolean
  consumptionTrackingType: string
  hasMods: boolean
  images: ChefMenuItemImage[]
}

export type ChefConcept = {
  id: string
  name: string
  isEnabled: boolean
  isApproved: boolean
  isUpdatedStructure: boolean
  groupOrderCutoffDays: string | null
  groupOrderCutoffTime: string | null
  groupOrderPickupTime: string | null
  marketType: string
  estimatedPricePerPerson: number | null
  margin: string
  tagsCuisineList: string
  tagsDayPartList: string
  individuallyPackaged: boolean
  hasMenuItemWithMods: boolean
  groupOrderMealTypes: string
  menuItems: ChefConceptMenuItem[]
}

export type ChefScheduledOrderItem = {
  id: string
  dietaryPreferences: string
  ingredients: string
  description: string
  item: string
  packaging: string | null
  totalPackaging: string | null
  quantity: number
  chefTotal: string | null
  isChild: boolean
  updates: string | null
}

export type ChefScheduledOrderMenuItem = {
  name: string
  itemType: string
}

export type ChefScheduledOrderMenu = {
  menuId: string
  menuName: string
  cutoffTime: string
  menuItems: ChefScheduledOrderMenuItem[]
}

export type ChefScheduledOrderPdf = {
  pdfType: string
  text: string
}

export type ChefScheduledOrderSalesRep = {
  name: string
  phone: string
}

export type ChefScheduledOrder = {
  id: string
  hqId: number
  status: string
  items: ChefScheduledOrderItem[]
  serviceItems: ChefScheduledOrderItem[]
  virtualItems: ChefScheduledOrderItem[]
  virtualKits: ChefScheduledOrderItem[]
  menus: ChefScheduledOrderMenu[]
  pickupTimes: string[]
  pdfs: ChefScheduledOrderPdf[]
  salesRep: ChefScheduledOrderSalesRep
  date: string
  grossPayout: string
  processingFee: string
  tax: string
  foodTaxRate: string
  servicesTaxRate: string | null
  totalPayout: string
  dietaryRestrictions: string
  dietaryPreferences: string
  orderNumber: string
  mealType: string
  instructions: string
  usesReusablePackages: boolean
  headCount: number
  deleted: boolean
  isFinal: boolean
  updates: string | null
  deleted_items: string | null
  is_accepted: number
  decline_reason: string
  cn_status: boolean
  weeklyChefEmailSentDateMap: any
  isMenu: boolean
  cutoffTime: string
}

export enum AddToExistingMenuCardProgressStatus {
  Select = 'select',
  Confirm = 'confirm',
}
